import React, { useRef, useState } from 'react';
import { Formik } from 'formik';
import Button from '../form-control/Button';
import Input from '../form-control/Input';
import TextArea from '../form-control/TextArea';
import * as Yup from "yup";
import { slackNotifyReport } from '../../api/requests';
import SelectOption from '../form-control/SelectOption';
import useUserAccount from '../../hooks/userAccountHook';
import SelectMenu from '../form-control/SelectMenu';
import SelectDropdown, { ISelectDropdownItem } from '../form-control/SelectDropdown';
import FileUpload from '../elements/FileUpload/FileUpload';
import useImage from '../../hooks/imageHook';

interface IContactSupportPayload {
  type: string;
  subject: string;
  description: string;
  from: string | undefined;
  company: string | undefined;
  companyType: string | undefined;
  attachment?: File | null;
  email: string | undefined;
  phone: string | undefined;
  priority: string;
}

type IReportIssueProps = {
  onSuccess: () => void;
}

const ReportFeatureBug = ({ onSuccess }: IReportIssueProps): JSX.Element => {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [attachment, setAttachment] = useState<File | null>(null);
  const { user } = useUserAccount();

  const serverity: ISelectDropdownItem[] = [
    { name: 'Low Priority', value: 'Low' },
    { name: 'Medium Priority', value: 'Medium' },
    { name: 'High Priority', value: 'High' },
    { name: 'Urgent', value: 'Urgent' }
  ];

  const sendSlackNotification = (reportResponse: IContactSupportPayload) => {
    const data: string = JSON.stringify({
      text: [
        `Type: *_${reportResponse?.type}_*`,
        `Priority: *_${reportResponse?.priority}_*`,
        `Subject: ${reportResponse?.subject}`,
        `Description: ${reportResponse?.description}`,
        `Url: ${window.location.href}`,
        `User: ${user?.fullName}`,
        `Company: ${user?.company?.name}`,
        `Company Type: ${user?.company?.type}`,
        `Email: ${user?.email}`,
        `${reportResponse?.type == 'Feature Requesting' ? 'Feature requester' : 'Issue reporter'}: ${reportResponse?.from}`
      ].join('\n')
    });
    slackNotifyReport(data);
  };

  const handleSubmit = async (values: any) => {
    const supportPayload: IContactSupportPayload = {
      type: values.type,
      subject: values.subject,
      description: values.description,
      from: user?.fullName,
      company: user?.company?.name,
      companyType: user?.company?.type,
      attachment: attachment,
      email: user?.email,
      phone: user?.phone,
      priority: values.priority,
    };

    try {
      await sendSlackNotification(supportPayload);
      onSuccess();
    } catch (error) {
      console.error('Error submitting support request:', error);
    } finally {
      setAttachment(null);
    }
  };


  return (
    <div className={`w-full max-w-3xl mx-auto bg-white dark:bg-darkBg rounded-lg shadow-sm overflow-hidden`}>
      <div className="px-6 py-4 border-b border-gray-100 dark:border-darkBg-border">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-800 dark:text-white">
              Contact Support
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Need help? Our support team is here to assist you with any questions or issues
            </p>
          </div>
        </div>
      </div>
      <div className="p-6 md:p-8">
        <Formik
          initialValues={{
            type: '',
            subject: '',
            description: '',
            priority: '',
            from: user?.fullName,
            company: user?.company?.name,
            companyType: user?.company?.type
          }}
          enableReinitialize={true}
          validateOnChange={true}
          validateOnBlur={true}
          validationSchema={Yup.object().shape({
            type: Yup.string().trim().required('Please select an issue type.'),
            subject: Yup.string().trim().required('Please enter a subject.'),
            description: Yup.string().trim().required('Please describe the issue.'),
            priority: Yup.string().trim().required('Please select a priority.')
          })}
          onSubmit={(values) => {
            const payload: IContactSupportPayload = {
              type: values.type,
              subject: values.subject,
              description: values.description,
              from: values.from,
              email: user?.email,
              phone: user?.phone,
              company: values.company,
              companyType: values.companyType,
              priority: values.priority,
              // attachment: attachment,
            
            };

            handleSubmit(payload);
            setSubmitting(true)
          }}>
          {({
            values, errors, isSubmitting, handleSubmit, handleChange, handleBlur, setFieldValue, touched, isValid }) => (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-6">
                <div className="grid grid-cols-1 gap-6">

                  <SelectMenu
                    onChange={(option: string) => setFieldValue('type', option)}
                    label='Support Type'
                    placeholder="Select type"
                    onBlur={handleBlur}
                    name="type"
                    listSelectedValue={values.type}
                    list={['General Inquiry', 'Technical Issue', 'Billing Question', 'Feature Request']}
                    helperText={touched.type && errors.type ? errors.type : undefined}
                  />
                  <Input
                    label="Subject"
                    name="subject"
                    value={values.subject}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Briefly describe how we can help you"
                    helperText={touched.subject && errors.subject ? errors.subject : undefined}
                  />

                  <SelectDropdown
                    onChange={(option: string) => setFieldValue('priority', option)}
                    label='Priority'
                    placeholder="Select priority"
                    onBlur={handleBlur}
                    name="priority"
                    listSelectedValue={values.priority}
                    list={serverity}
                    helperText={touched.priority && errors.priority ? errors.priority : undefined}
                  />

                  <TextArea
                    label="Description"
                    name="description"
                    value={values.description}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Please provide detailed information about your inquiry or issue..."
                    error={touched.description ? errors.description : undefined}
                    helperText={touched.description && errors.description ? errors.description : undefined}
                  />

{/* 
                  <FileUpload
                    onFileLoad={(images: any) => {
                      if (images && images[0]) {
                        setAttachment(images[0]);
                      }
                    }}
                    showFileMax={false}
                    savedImage={attachment}
                    onFileDeleted={() => {
                      setAttachment(null);
                    }}
                    imageAlt={'Attachment'}
                  /> */}
                </div>

                <div className="flex justify-end pt-4 border-t border-gray-100">
                  <Button
                    label="Send Message"
                    type="submit"
                    className="w-fit px-4 py-2 rounded border bg-primary-main"
                    curved
                    bgColor="primary"
                    loading={submitting}
                    disabled={submitting}
                  />
                </div>
              </div>
            </form>
          )}
        </Formik>
      </div>
      <div className="bg-gray-50 px-6 py-3 text-xs text-gray-500 border-t border-gray-100">
        <p>Our support team will get back to you as soon as possible. Thank you for reaching out!</p>
      </div>
    </div>
  );
};



export default ReportFeatureBug;
